// lib
import { useMemo, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'

import { getFilteredQuestions } from '@aytm/app/stats/page/comments/lib/utils'

import useExport from '@aytm/app/stats/page/sidebar/components/hooks/useExport'
import useVariables from '@aytm/app/stats/page/sidebar/components/hooks/useVariables'
import useAttachments from '@aytm/app/stats/page/sidebar/components/hooks/useAttachments'
import useCurrentReport from '@aytm/app/stats/page/sidebar/components/hooks/useCurrentReport'
import useViewOptionsShortcuts from '@aytm/app/stats/page/sidebar/components/hooks/useViewOptionsShortcuts'

import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/Sidebar'
import { clearedFiltersEdit } from '@aytm/app/stats/page/sidebar/components/reducers'

// components
import CodingPanel from '@aytm/app/stats/page/sidebar/coding/CodingPanel'
import ColorOptionsPanel from '@aytm/app/stats/page/sidebar/color-options/ColorOptionsPanel'
import CompletesPanel from '@aytm/app/stats/page/sidebar/completes/CompletesPanel'
import DataQualityPanel from '@aytm/app/stats/page/sidebar/data-quality/DataQualityPanel'
import DataLabPanel from '@aytm/app/stats/page/sidebar/data-lab/DataLabPanel'
import FiltersPanel from '@aytm/app/stats/page/sidebar/filters/FiltersPanel'
import MappingPanel from '@aytm/app/stats/page/sidebar/mapping/MappingPanel'
import ReportsPanel from '@aytm/app/stats/page/sidebar/reports/ReportsPanel'
import TargetPanel from '@aytm/app/stats/page/sidebar/target/TargetPanel'
import ViewOptionsPanel from '@aytm/app/stats/page/sidebar/view-options/ViewOptionsPanel'
import WeightingPanel from '@aytm/app/stats/page/sidebar/weighting/WeightingPanel'
import ExportPanel from '@aytm/app/stats/page/sidebar/export/ExportPanel'

const SidebarContent = ({
  container,
  sidebar,
  survey,
  model,
  parser,
  features,
  currentPanel,
  getReportFilters,
  onParamsChange,
  onFetch,
  onTagsFetch,
  onFetchApiKey,
  onModeChange,
  onExport,
  onQuarantinedExport,
  onDNDStart,
  onDNDEnd,
  onPanelChange,
  onReportsChange,
  onReportsError,
  onReportIdChange,
  repaintReport,
  onPalettesChange,
  addQuestion,
  addPinnedQuestion,
  removePinnedQuestion,
  onScrollToQuestion,
  onGenerateArtificialResponses
}) => {
  const { questions } = survey

  const { sidebarDispatch } = useContext(SidebarDataContext)

  const report = useCurrentReport(model.reports, model.reportId)

  useViewOptionsShortcuts({ survey, model, report, features, onParamsChange })

  const filteredQuestions = useMemo(() => getFilteredQuestions(questions), [questions])

  const { onExportStart } = useExport({ dispatch: sidebarDispatch, onExport })
  const { onVariableUpload, onVariableRemove, getVariableError, getVariableProcessing } = useVariables({
    dispatch: sidebarDispatch,
    onFetch,
    onFetchApiKey
  })
  const { onAttachmentUpload, onAttachmentRemove } = useAttachments({ dispatch: sidebarDispatch })

  // another report brings its own applied filter, so an edit left unapplied on the previous one goes too,
  // even when the reader comes back to that report
  useEffect(
    () =>
      sidebarDispatch({
        type: 'UPDATE_STATE',
        key: 'filters',
        state: {
          traits: { collapsed: false },
          questions: { collapsed: false },
          ...clearedFiltersEdit
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [model.reportId, sidebarDispatch]
  )

  return (
    <>
      {'target' == currentPanel && <TargetPanel survey={survey} model={model} />}
      <ReportsPanel
        model={model}
        report={report}
        features={features}
        isHidden={'reports' != currentPanel}
        getReportFilters={getReportFilters}
        onModeChange={onModeChange}
        onPanelChange={onPanelChange}
        onReportsChange={onReportsChange}
        onReportsError={onReportsError}
        onReportIdChange={onReportIdChange}
        repaintReport={repaintReport}
      />
      {'view-options' == currentPanel && (
        <ViewOptionsPanel
          survey={survey}
          model={model}
          report={report}
          features={features}
          onModeChange={onModeChange}
          onParamsChange={onParamsChange}
          onGenerateArtificialResponses={onGenerateArtificialResponses}
        />
      )}
      {'coding' == currentPanel && (
        <CodingPanel
          survey={survey}
          model={model}
          features={features}
          filteredQuestions={filteredQuestions}
          onFetch={onFetch}
          onTagsFetch={onTagsFetch}
          onFetchApiKey={onFetchApiKey}
          onModeChange={onModeChange}
          onParamsChange={onParamsChange}
        />
      )}
      {'filters' == currentPanel && (
        <FiltersPanel
          survey={survey}
          model={model}
          report={report}
          parser={parser}
          features={features}
          onDNDStart={onDNDStart}
          onDNDEnd={onDNDEnd}
          addQuestion={addQuestion}
          onVariableUpload={onVariableUpload}
          onVariableRemove={onVariableRemove}
          onModeChange={onModeChange}
          onParamsChange={onParamsChange}
          onScrollToQuestion={onScrollToQuestion}
          getVariableError={getVariableError}
          getVariableProcessing={getVariableProcessing}
        />
      )}
      {'data-lab' == currentPanel && (
        <DataLabPanel
          sidebar={sidebar}
          model={model}
          features={features}
          addQuestion={addQuestion}
          addPinnedQuestion={addPinnedQuestion}
          removePinnedQuestion={removePinnedQuestion}
          onModeChange={onModeChange}
          onDNDStart={onDNDStart}
          onDNDEnd={onDNDEnd}
        />
      )}
      {'weighting' == currentPanel && (
        <WeightingPanel
          survey={survey}
          model={model}
          features={features}
          onModeChange={onModeChange}
          onParamsChange={onParamsChange}
        />
      )}
      {'data-quality' == currentPanel && (
        <DataQualityPanel root={container} model={model} features={features} onExport={onQuarantinedExport} />
      )}
      {'color-options' == currentPanel && (
        <ColorOptionsPanel
          container={container}
          model={model}
          onModeChange={onModeChange}
          onParamsChange={onParamsChange}
          onPalettesChange={onPalettesChange}
        />
      )}
      {'export' == currentPanel && (
        <ExportPanel
          survey={survey}
          model={model}
          report={report}
          features={features}
          filteredQuestions={filteredQuestions}
          onExport={onExportStart}
          onModeChange={onModeChange}
          onParamsChange={onParamsChange}
          onAttachmentUpload={onAttachmentUpload}
          onAttachmentRemove={onAttachmentRemove}
        />
      )}
      {'mapping' == currentPanel && (
        <MappingPanel
          sidebar={sidebar}
          model={model}
          features={features}
          addQuestion={addQuestion}
          addPinnedQuestion={addPinnedQuestion}
          removePinnedQuestion={removePinnedQuestion}
          onModeChange={onModeChange}
          onDNDStart={onDNDStart}
          onDNDEnd={onDNDEnd}
        />
      )}
      {'completes' == currentPanel && <CompletesPanel model={model} onParamsChange={onParamsChange} />}
    </>
  )
}

SidebarContent.propTypes = {
  container: PropTypes.any,
  sidebar: PropTypes.any,
  survey: PropTypes.object,
  model: PropTypes.object,
  parser: PropTypes.any,
  features: PropTypes.object,
  currentPanel: PropTypes.string,
  getReportFilters: PropTypes.func,
  onParamsChange: PropTypes.func,
  onFetch: PropTypes.func,
  onTagsFetch: PropTypes.func,
  onFetchApiKey: PropTypes.func,
  onModeChange: PropTypes.func,
  onExport: PropTypes.func,
  onQuarantinedExport: PropTypes.func,
  onDNDStart: PropTypes.func,
  onDNDEnd: PropTypes.func,
  onPanelChange: PropTypes.func,
  onReportsChange: PropTypes.func,
  onReportsError: PropTypes.func,
  onReportIdChange: PropTypes.func,
  repaintReport: PropTypes.func,
  onPalettesChange: PropTypes.func,
  addQuestion: PropTypes.func,
  addPinnedQuestion: PropTypes.func,
  removePinnedQuestion: PropTypes.func,
  onScrollToQuestion: PropTypes.func,
  onGenerateArtificialResponses: PropTypes.func
}

SidebarContent.displayName = 'SidebarContent'

export default SidebarContent
